import * as React from "react";
import { createPortal } from "react-dom";
import MobileDetect from "mobile-detect";

import { AnimationWrapper } from "./phone-container-style";
import ScreenContentRenderer from "../../views/screen-content-renderer/screen-content-renderer";
import type { AppNavigationModel, NavigationAnimation } from "../../../lib/navigation-model";
import VelocityTracker from "../../../lib/velocity-tracker";
import { PUSH_MODAL_ROOT } from "../../../lib/constants";
import type { TabDescription } from "common/generator/description";
import ScreenAnimator from "./private/screen-animator";
import type { EvaluatedScreen, RenderEnvironment } from "common/components/types";
import { ScreenFlag } from "common/components/SerializedApp";
import { ignore } from "common/Support";

interface Props {
    readonly currentScreen: EvaluatedScreen;
    readonly hasTabBar: boolean;
    readonly renderEnvironment: RenderEnvironment;
    readonly currentNavigationModel: AppNavigationModel;
    readonly currentTab: TabDescription;
}

const handleBack = new MobileDetect(window.navigator.userAgent).is("AndroidOS");

const ariaHiddenDiv = (
    c: React.ReactNode,
    hidden: boolean,
    idSuffix: string,
    model?: AppNavigationModel
): React.ReactNode => {
    if (model === undefined || c === undefined) return undefined;

    const screen = model.currentScreen;
    if (screen === undefined) return undefined;

    const id = screen.screenId + model.currentScreenDepth + idSuffix;

    return (
        <div id={id} key={id} aria-hidden={hidden}>
            {c}
        </div>
    );
};

const PhoneContainer: React.VFC<Props> = p => {
    const { hasTabBar, renderEnvironment } = p;

    const [currentNavModel, setCurrentNavModel] = React.useState<AppNavigationModel>(p.currentNavigationModel);
    const [previousNavigationModel, setPreviousNavigationModel] = React.useState<AppNavigationModel | undefined>(
        undefined
    );

    const [animating, setAnimating] = React.useState(false);
    const [dragging, setDragging] = React.useState(false);
    const [dragFinishing, setDragFinishing] = React.useState(false);
    const [dragStart, setDragStart] = React.useState(0);
    const [dragPosition, setDragPosition] = React.useState(0);
    const [popWhenDone, setPopWhenDone] = React.useState(false);
    const [popHandled, setPopHandled] = React.useState(false);
    const [hasPopState, setHasPopState] = React.useState(false);

    const screenWrapperRef = React.useRef<HTMLDivElement>(null);
    const velocityTracker = React.useMemo(() => {
        return new VelocityTracker();
    }, []);

    React.useEffect(() => {
        if (handleBack) {
            const onPopState = () => {
                setHasPopState(false);

                const nav = currentNavModel;
                if (nav?.canCurrentScreenPop || nav?.canCurrentScreenPopModal) {
                    renderEnvironment.appEnvironment.popAnyScreen(renderEnvironment);
                }
            };

            window.addEventListener("popstate", onPopState);

            return () => window.removeEventListener("popstate", onPopState);
        }

        return ignore;
    }, [currentNavModel, renderEnvironment]);

    /**
     * Direct port from `getDerivedStateFromProps`.
     * This updates need to happen synchoronously _during_ render
     * to mimic the behavior of getDerivedStateFromProps.
     *
     * It's likely avoidable, but that's for another issue.
     */
    const getDerivedStateFromProps = () => {
        if (
            p.currentNavigationModel !== undefined &&
            p.currentNavigationModel.currentScreen?.screenId !== currentNavModel.currentScreen?.screenId
        ) {
            const newPreviousNavigationModal = currentNavModel;
            setPreviousNavigationModel(newPreviousNavigationModal);

            const newCurrentNavModel = p.currentNavigationModel;
            setCurrentNavModel(newCurrentNavModel);

            if (newPreviousNavigationModal !== undefined && !popHandled) {
                setAnimating(newCurrentNavModel.getAnimation(newPreviousNavigationModal) !== "none");
            }
            setPopHandled(false);

            if (
                (newCurrentNavModel.canCurrentScreenPop || newCurrentNavModel.canCurrentScreenPopModal) &&
                !hasPopState &&
                handleBack
            ) {
                setHasPopState(true);
                window.history.pushState({}, "");
            }
        } else if (p.currentNavigationModel !== undefined && p.currentNavigationModel !== currentNavModel) {
            setCurrentNavModel(p.currentNavigationModel);
        }
    };
    getDerivedStateFromProps();

    const handleAnimationEnd = React.useCallback(() => {
        const isPopWhenDone = popWhenDone;
        setAnimating(false);
        setDragFinishing(false);
        setPopWhenDone(false);
        setPopHandled(popWhenDone);
        if (isPopWhenDone) {
            renderEnvironment.appEnvironment.popScreen(renderEnvironment);
        }
    }, [renderEnvironment, popWhenDone]);

    const onTouchStart = React.useCallback(
        (ev: React.TouchEvent<HTMLDivElement>) => {
            const navModel = currentNavModel;

            if (navModel === undefined) return;

            const isSubScreen = navModel.canCurrentScreenPop;

            const x = ev.touches[0].clientX;
            if (x < 50 && isSubScreen && screenWrapperRef.current !== null) {
                setPreviousNavigationModel(navModel.popScreen());
                setDragging(true);
                setDragStart(x);
                setDragPosition(0);
            }
        },
        [currentNavModel, screenWrapperRef]
    );

    const onTouchMove = React.useCallback(
        (ev: React.TouchEvent<HTMLDivElement>) => {
            if (screenWrapperRef.current === null) return;
            const x = ev.touches[0].clientX;
            const width = screenWrapperRef.current.clientWidth;
            const distance = x - dragStart;

            // nothing has ever gone wrong by clamping to 1% and 99% to avoid div by zeros
            const deltaWidth = Math.max(0.01, Math.min(0.99, distance / width));
            velocityTracker.addPosition(deltaWidth);
            setDragPosition(deltaWidth);
        },
        [dragStart, screenWrapperRef, velocityTracker]
    );

    const onTouchCancel = React.useCallback((_ev: React.TouchEvent<HTMLDivElement>) => {
        setDragging(false);
    }, []);

    const onTouchEnd = React.useCallback(
        (_ev: React.TouchEvent<HTMLDivElement>) => {
            setDragging(false);
            setDragFinishing(dragPosition > 0 && dragPosition < 1);

            const rawVel = velocityTracker.getVelocity();

            if (dragPosition > 0.4 || rawVel > 2) {
                setPopWhenDone(true);
            }
        },
        [dragPosition, velocityTracker]
    );

    const makeNode = React.useCallback(
        (
            model: AppNavigationModel,
            mustDrawBelow: boolean,
            dragOffset: number | undefined,
            dragOpacity: number | undefined
        ): React.ReactNode => {
            const animation: NavigationAnimation =
                previousNavigationModel !== undefined && animating
                    ? currentNavModel.getAnimation(previousNavigationModel)
                    : "none";

            const screen = model.currentScreen;
            if (screen === undefined) return null;

            const id = screen.screenId + model.currentScreenDepth;
            const isModal = model.isInModal();
            const isCurrent = currentNavModel?.currentScreen === screen;

            return (
                <ScreenAnimator
                    key={id}
                    dragOffset={dragOffset}
                    dragOpacity={dragOpacity}
                    dragFinishing={dragFinishing}
                    animation={animation}
                    onHidden={handleAnimationEnd}
                    noScroll={dragging}
                    forceRender={!isCurrent && mustDrawBelow}
                    present={currentNavModel.currentScreen === screen}>
                    <ScreenContentRenderer
                        key={id + "screen"}
                        screen={screen}
                        inTab={hasTabBar && !isModal}
                        isSubScreen={model.canCurrentScreenPop}
                        renderEnvironment={renderEnvironment.with(undefined, screen.flags)}
                        currentTab={p.currentTab}
                    />
                </ScreenAnimator>
            );
        },
        [
            previousNavigationModel,
            animating,
            currentNavModel,
            dragging,
            dragFinishing,
            handleAnimationEnd,
            hasTabBar,
            renderEnvironment,
            p.currentTab,
        ]
    );

    const computeDragDistance = React.useCallback(() => {
        let dragDistance: number | undefined;
        let bottomDistance: number | undefined;
        let bottomOpacity: number | undefined;
        if (dragging || dragFinishing) {
            dragDistance = dragPosition;
            bottomOpacity = 0.8 + dragPosition * 0.2;
            dragDistance *= 100;

            if (dragFinishing && popWhenDone) {
                dragDistance = 100;
                bottomOpacity = 1;
            } else if (dragFinishing && !popWhenDone) {
                dragDistance = 0;
                bottomOpacity = 0.8;
            }

            bottomDistance = -25 + dragDistance / 4;
        }

        return [dragDistance, bottomDistance, bottomOpacity];
    }, [dragFinishing, dragPosition, dragging, popWhenDone]);

    const makeModal = React.useCallback((b: React.ReactNode, t?: React.ReactNode): React.ReactNode => {
        const target = document.getElementById(PUSH_MODAL_ROOT);
        if (target === null) return null;
        return createPortal(
            <>
                {b}
                {t}
            </>,
            target
        );
    }, []);

    let topModel: AppNavigationModel;
    let bottomModel: AppNavigationModel | undefined;

    const mustDrawbelow =
        animating || dragging || dragFinishing || currentNavModel.currentScreenHasFlag(ScreenFlag.IsFinalTransient);

    if (previousNavigationModel === undefined || !mustDrawbelow) {
        topModel = currentNavModel;
    } else {
        const previousScreen = previousNavigationModel.currentScreen;
        const currentOnTop = previousScreen !== undefined && currentNavModel.hasScreen(previousScreen);
        topModel = currentOnTop ? currentNavModel : previousNavigationModel;
        bottomModel = currentOnTop ? previousNavigationModel : currentNavModel;
    }

    const [dragDistance, bottomDistance, bottomOpacity] = computeDragDistance();

    const topNode = makeNode(topModel, mustDrawbelow, dragDistance, undefined);
    let bottomNode: React.ReactNode;
    if (bottomModel !== undefined) {
        bottomNode = makeNode(bottomModel, mustDrawbelow, bottomDistance, bottomOpacity);
    }

    const topInModal = topModel.isInModal();
    const bottomInModal = bottomModel?.isInModal();

    let content: React.ReactNode;
    let portalContent: React.ReactNode;
    if (topInModal && bottomInModal === true) {
        portalContent = makeModal(
            ariaHiddenDiv(bottomNode, true, "portal", bottomModel),
            ariaHiddenDiv(topNode, false, "portal", topModel)
        );
    } else {
        // content must always use a fragment even with a single child or
        // you will get unneeded remounting of the component which results
        // in ugly visual hitching
        if (topInModal) {
            portalContent = makeModal(ariaHiddenDiv(topNode, false, "portal", topModel));
            content =
                bottomNode === undefined ? undefined : <>{ariaHiddenDiv(bottomNode, true, "content", bottomModel)}</>;
        } else if (bottomInModal === true && bottomNode !== undefined) {
            portalContent = makeModal(ariaHiddenDiv(bottomNode, false, "portal", bottomModel));
            content = topNode === undefined ? undefined : <>{ariaHiddenDiv(topNode, true, "content", topModel)}</>;
        } else {
            content = (
                <>
                    {ariaHiddenDiv(bottomNode, true, "content", bottomModel)}
                    {ariaHiddenDiv(topNode, false, "content", topModel)}
                </>
            );
        }
    }

    const dragProps: any = {
        onTouchStart: onTouchStart,
    };

    if (dragging) {
        dragProps.onTouchMove = onTouchMove;
        dragProps.onTouchEnd = onTouchEnd;
        dragProps.onTouchCancel = onTouchCancel;
    }

    return (
        <AnimationWrapper ref={screenWrapperRef} {...dragProps}>
            {content}
            {portalContent}
        </AnimationWrapper>
    );
};
export default PhoneContainer;
